import React from 'react'
import { Language } from './data/types'

interface ArchitectureDiagramProps {
    language: Language
}

const ArchitectureDiagram: React.FC<ArchitectureDiagramProps> = ({ language }) => {
    const layers = [
        {
            title: language === 'pt' ? 'Admin (Parceiro)' : 'Admin (Partner)',
            subtitle: language === 'pt' ? 'Empresas integradoras' : 'Integrating companies',
            icon: '🏢',
            color: '#10b981',
            examples: language === 'pt' ? 'Empresas parceiras' : 'Partner companies'
        },
        {
            title: language === 'pt' ? 'Autenticação' : 'Authentication',
            subtitle: 'POST /admin/get-token',
            icon: '🔑',
            color: '#6a737d',
            examples: 'JWT Token (1h)'
        },
        {
            title: language === 'pt' ? 'Client (Usuário)' : 'Client (User)',
            subtitle: language === 'pt' ? 'Usuários finais' : 'End users',
            icon: '👤',
            color: '#2e7d32',
            examples: language === 'pt' ? 'Solicitações de empréstimo' : 'Loan requests'
        },
        {
            title: 'API EmpreX',
            subtitle: language === 'pt' ? 'Serviços financeiros' : 'Financial services',
            icon: '🏦',
            color: '#f57c00',
            examples: language === 'pt' ? 'Empréstimos, Pagamentos, KYC' : 'Loans, Payments, KYC'
        },
        {
            title: language === 'pt' ? 'Integrações' : 'Integrations',
            subtitle: language === 'pt' ? 'Serviços externos' : 'External services',
            icon: '🔗',
            color: '#6a737d',
            examples: language === 'pt' ? 'Módulo de Pagamento, KYC, Bancário' : 'Payment Module, KYC Module, Banking Module'
        }
    ]

    return (
        <div style={{
            backgroundColor: '#f6f8fa',
            padding: '40px',
            borderRadius: '12px',
            marginBottom: '24px'
        }}>
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '20px'
            }}>
                {layers.map((layer, index) => (
                    <React.Fragment key={index}>
                        {/* Layer box */}
                        <div style={{
                            width: '100%',
                            maxWidth: '600px',
                            background: `linear-gradient(135deg, ${layer.color}15 0%, ${layer.color}08 100%)`,
                            border: `3px solid ${layer.color}`,
                            borderRadius: '12px',
                            padding: '20px 24px',
                            boxShadow: `0 4px 16px ${layer.color}30`,
                            transition: 'all 0.3s ease'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                <div style={{
                                    fontSize: '36px',
                                    minWidth: '48px',
                                    textAlign: 'center'
                                }}>
                                    {layer.icon}
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div style={{
                                        fontSize: '18px',
                                        fontWeight: 700,
                                        color: layer.color,
                                        marginBottom: '4px'
                                    }}>
                                        {layer.title}
                                    </div>
                                    <div style={{
                                        fontSize: '13px',
                                        fontFamily: 'Monaco, Menlo, monospace',
                                        color: '#24292e',
                                        marginBottom: '6px',
                                        fontWeight: 600
                                    }}>
                                        {layer.subtitle}
                                    </div>
                                    <div style={{
                                        fontSize: '12px',
                                        color: '#6a737d',
                                        fontStyle: 'italic'
                                    }}>
                                        {layer.examples}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Arrow between layers */}
                        {index < layers.length - 1 && (
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '4px'
                            }}>
                                <div style={{
                                    width: '3px',
                                    height: '30px',
                                    background: 'linear-gradient(180deg, #10b981 0%, #34d399 100%)'
                                }} />
                                <div style={{
                                    width: 0,
                                    height: 0,
                                    borderLeft: '8px solid transparent',
                                    borderRight: '8px solid transparent',
                                    borderTop: '12px solid #34d399'
                                }} />
                            </div>
                        )}
                    </React.Fragment>
                ))}
            </div>

            {/* Data flow indicators */}
            <div style={{
                marginTop: '32px',
                padding: '20px',
                backgroundColor: '#fff',
                borderRadius: '8px',
                border: '1px solid #e1e4e8'
            }}>
                <div style={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#24292e',
                    marginBottom: '12px'
                }}>
                    {language === 'pt' ? '📊 Fluxo de Dados' : '📊 Data Flow'}
                </div>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '12px',
                    fontSize: '13px'
                }}>
                    <div style={{ color: '#6a737d' }}>
                        <strong style={{ color: '#10b981' }}>↓</strong> {language === 'pt' ? 'Requisições' : 'Requests'}
                    </div>
                    <div style={{ color: '#6a737d' }}>
                        <strong style={{ color: '#2e7d32' }}>↑</strong> {language === 'pt' ? 'Respostas' : 'Responses'}
                    </div>
                    <div style={{ color: '#6a737d' }}>
                        <strong style={{ color: '#f57c00' }}>⟷</strong> {language === 'pt' ? 'Webhooks' : 'Webhooks'}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ArchitectureDiagram
